import React, { useState, useEffect } from 'react';
import { X, Plus, MessageSquare, Send, Trash2, Lock, Eye, Globe, Reply } from 'lucide-react';
import { dataRoomAPI } from '../../../services/api';

const CATEGORIES = ['Legal', 'Financial', 'Commercial', 'Other'];
const PRIORITIES = ['low', 'medium', 'high'];
const CATEGORY_COLORS = {
  Legal: 'bg-purple-50 text-purple-700',
  Financial: 'bg-emerald-50 text-emerald-700',
  Commercial: 'bg-blue-50 text-blue-700',
  Other: 'bg-gray-100 text-gray-700',
};
const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-amber-50 text-amber-700',
  high: 'bg-red-50 text-red-700',
};
const VISIBILITY_OPTIONS = [
  { value: 'published', label: 'Published', icon: Globe },
  { value: 'private', label: 'Private (asker only)', icon: Eye },
  { value: 'internal', label: 'Internal (AMC team only)', icon: Lock },
];

export default function QAModal({ open, onClose, dataRoomId }) {
  const [view, setView] = useState('list');
  const [scope, setScope] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [questions, setQuestions] = useState([]);
  const [activeQ, setActiveQ] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && view === 'list') load();
  }, [open, view, scope, category]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await dataRoomAPI.listQuestions(dataRoomId, {
        scope: scope || undefined,
        category: category || undefined,
        search: search || undefined,
      });
      setQuestions(res.data.data || []);
    } finally {
      setLoading(false);
    }
  };

  const openQuestion = async (id) => {
    try {
      const res = await dataRoomAPI.getQuestion(dataRoomId, id);
      setActiveQ(res.data);
      setView('detail');
    } catch (e) {
      alert('Failed to open question');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" data-testid="qa-modal">
      <div className="bg-white rounded-lg w-full max-w-5xl h-[90vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <MessageSquare size={18} className="text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Q&A</h2>
          </div>
          <div className="flex items-center gap-2">
            {view === 'list' && (
              <button
                onClick={() => { setView('new'); setActiveQ(null); }}
                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700"
                data-testid="new-question-btn"
              >
                <Plus size={12} className="mr-1" /> New Question
              </button>
            )}
            {view !== 'list' && (
              <button
                onClick={() => { setView('list'); setActiveQ(null); }}
                className="text-xs text-blue-600 hover:underline"
              >
                ← Back to list
              </button>
            )}
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700"><X size={18} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {view === 'list' && (
            <QuestionsList
              questions={questions}
              loading={loading}
              scope={scope}
              setScope={setScope}
              category={category}
              setCategory={setCategory}
              search={search}
              setSearch={setSearch}
              onOpen={openQuestion}
              onRefresh={load}
            />
          )}
          {view === 'new' && (
            <NewQuestionForm
              dataRoomId={dataRoomId}
              onDone={() => { setView('list'); load(); }}
              onCancel={() => setView('list')}
            />
          )}
          {view === 'detail' && activeQ && (
            <QuestionDetail
              dataRoomId={dataRoomId}
              question={activeQ.question}
              answers={activeQ.answers}
              onRefresh={() => openQuestion(activeQ.question.id)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function QuestionsList({ questions, loading, scope, setScope, category, setCategory, search, setSearch, onOpen, onRefresh }) {
  return (
    <div className="p-5">
      <div className="flex flex-wrap items-end gap-2 mb-4 pb-3 border-b">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Scope</label>
          <select value={scope} onChange={e => setScope(e.target.value)} className="border rounded px-2 py-1 text-xs" data-testid="qa-scope">
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="answered">Answered</option>
            <option value="closed">Closed</option>
            <option value="mine">My Questions</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Category</label>
          <select value={category} onChange={e => setCategory(e.target.value)} className="border rounded px-2 py-1 text-xs" data-testid="qa-category">
            <option value="">All</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-gray-500 mb-1">Search</label>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onRefresh()}
            placeholder="Search questions..."
            className="w-full border rounded px-2 py-1 text-xs"
            data-testid="qa-search"
          />
        </div>
        <button onClick={onRefresh} className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700" data-testid="qa-apply">
          Apply
        </button>
      </div>
      {loading ? (
        <div className="py-8 text-center text-gray-500 text-sm">Loading...</div>
      ) : questions.length === 0 ? (
        <div className="py-8 text-center text-gray-500 text-sm">No questions yet.</div>
      ) : (
        <div className="space-y-2" data-testid="qa-list">
          {questions.map(q => (
            <div
              key={q.id}
              onClick={() => onOpen(q.id)}
              className="border rounded-lg p-3 hover:border-blue-300 hover:shadow-sm cursor-pointer"
              data-testid={`qa-row-${q.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 truncate">{q.title}</h3>
                  <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{q.body}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 ml-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${CATEGORY_COLORS[q.category]}`}>{q.category}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${PRIORITY_COLORS[q.priority]}`}>{q.priority}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-500">
                <span className="inline-flex items-center gap-1"><Reply size={10} />{q.answer_count || 0} answers</span>
                <span>{new Date(q.created_at).toLocaleDateString()}</span>
                <span>by {q.author_email}</span>
                <span className={`ml-auto px-1.5 py-0.5 rounded text-[10px] ${q.status === 'answered' ? 'bg-emerald-50 text-emerald-700' : q.status === 'closed' ? 'bg-gray-100 text-gray-600' : 'bg-amber-50 text-amber-700'}`}>
                  {q.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewQuestionForm({ dataRoomId, onDone, onCancel }) {
  const [form, setForm] = useState({ title: '', body: '', category: 'Other', priority: 'medium', assigned_to: '' });
  const [saving, setSaving] = useState(false);
  const [taggableUsers, setTaggableUsers] = useState([]);

  useEffect(() => {
    dataRoomAPI.getTaggableUsers(dataRoomId).then(r => setTaggableUsers(r.data.users || [])).catch(() => {});
  }, [dataRoomId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return;
    setSaving(true);
    try {
      await dataRoomAPI.createQuestion(dataRoomId, {
        ...form,
        assigned_to: form.assigned_to || undefined,
      });
      onDone();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-5 space-y-4 max-w-2xl">
      <h3 className="font-semibold text-gray-900">New Question</h3>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
        <input
          type="text"
          value={form.title}
          onChange={e => setForm({ ...form, title: e.target.value })}
          className="w-full border rounded-lg px-3 py-2 text-sm"
          required
          data-testid="new-q-title"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Body *</label>
        <textarea
          value={form.body}
          onChange={e => setForm({ ...form, body: e.target.value })}
          rows={5}
          className="w-full border rounded-lg px-3 py-2 text-sm"
          required
          data-testid="new-q-body"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm">
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
          <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm">
            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Tag a user (optional)</label>
        <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="new-q-assign">
          <option value="">-- Select user --</option>
          {taggableUsers.filter(u => u.type === 'internal').length > 0 && (
            <optgroup label="AMC Team">
              {taggableUsers.filter(u => u.type === 'internal').map(u => (
                <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
              ))}
            </optgroup>
          )}
          {taggableUsers.filter(u => u.type === 'external').length > 0 && (
            <optgroup label="External Users">
              {taggableUsers.filter(u => u.type === 'external').map(u => (
                <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
              ))}
            </optgroup>
          )}
        </select>
      </div>
      <div className="flex gap-2 pt-2 border-t">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
        <button type="submit" disabled={saving} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50" data-testid="new-q-submit">
          <Send size={14} className="mr-1" /> {saving ? 'Submitting...' : 'Submit Question'}
        </button>
      </div>
    </form>
  );
}

function QuestionDetail({ dataRoomId, question, answers, onRefresh }) {
  const [replyBody, setReplyBody] = useState('');
  const [visibility, setVisibility] = useState('published');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!replyBody.trim()) return;
    setSubmitting(true);
    try {
      await dataRoomAPI.createAnswer(dataRoomId, question.id, { body: replyBody, visibility });
      setReplyBody('');
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteAnswer = async (id) => {
    if (!window.confirm('Delete answer?')) return;
    await dataRoomAPI.deleteAnswer(dataRoomId, id);
    onRefresh();
  };

  const close = async () => {
    await dataRoomAPI.updateQuestion(dataRoomId, question.id, { status: 'closed' });
    onRefresh();
  };

  return (
    <div className="p-5 max-w-3xl">
      <div className="border rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${CATEGORY_COLORS[question.category]}`}>{question.category}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${PRIORITY_COLORS[question.priority]}`}>{question.priority}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${question.status === 'answered' ? 'bg-emerald-50 text-emerald-700' : question.status === 'closed' ? 'bg-gray-100 text-gray-600' : 'bg-amber-50 text-amber-700'}`}>
            {question.status}
          </span>
          <span className="text-[11px] text-gray-500 ml-auto">{new Date(question.created_at).toLocaleString()}</span>
        </div>
        <h2 className="font-semibold text-gray-900 text-lg mb-1">{question.title}</h2>
        <p className="text-sm text-gray-700 whitespace-pre-wrap">{question.body}</p>
        <p className="text-[11px] text-gray-500 mt-2">Asked by {question.author_email}</p>
        {question.status !== 'closed' && (
          <button onClick={close} className="text-[11px] text-gray-500 hover:text-gray-800 mt-2 underline" data-testid="close-question-btn">
            Close question
          </button>
        )}
      </div>

      <h3 className="font-semibold text-sm text-gray-800 mb-2">Answers ({answers.length})</h3>
      <div className="space-y-3 mb-4">
        {answers.map(a => {
          const vOpt = VISIBILITY_OPTIONS.find(v => v.value === a.visibility) || VISIBILITY_OPTIONS[0];
          const VIcon = vOpt.icon;
          return (
            <div key={a.id} className={`border rounded-lg p-3 ${a.visibility === 'internal' ? 'bg-amber-50/30 border-amber-200' : a.visibility === 'private' ? 'bg-blue-50/30 border-blue-200' : 'bg-white'}`} data-testid={`answer-${a.id}`}>
              <div className="flex items-center gap-2 mb-2 text-[11px] text-gray-500">
                <VIcon size={11} />
                <span className="font-medium">{vOpt.label}</span>
                <span className="ml-auto">{new Date(a.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{a.body}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[11px] text-gray-500">by {a.author_email}</span>
                <button onClick={() => deleteAnswer(a.id)} className="p-1 text-gray-400 hover:text-red-500 ml-auto" data-testid={`delete-answer-${a.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={submit} className="border rounded-lg p-3 bg-gray-50">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Add Answer</h4>
        <textarea
          value={replyBody}
          onChange={e => setReplyBody(e.target.value)}
          rows={3}
          placeholder="Your answer..."
          className="w-full border rounded px-2 py-1.5 text-sm bg-white"
          required
          data-testid="new-answer-body"
        />
        <div className="flex items-center justify-between mt-2">
          <select
            value={visibility}
            onChange={e => setVisibility(e.target.value)}
            className="border rounded px-2 py-1 text-xs"
            data-testid="answer-visibility"
          >
            {VISIBILITY_OPTIONS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
          </select>
          <button type="submit" disabled={submitting} className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50" data-testid="submit-answer">
            <Send size={12} className="mr-1" /> {submitting ? 'Posting...' : 'Post Answer'}
          </button>
        </div>
      </form>
    </div>
  );
}
